import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

// Polling fallback when WebSocket isn't available
const useFallbackPolling = (enabled, onMessage) => {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const poll = async () => {
      try {
        const [treeRes, gameRes, raffleRes] = await Promise.all([
          axios.get(`${API}/tree/state`).catch(() => ({ data: null })),
          axios.get(`${API}/game/state`).catch(() => ({ data: null })),
          axios.get(`${API}/raffle/state`).catch(() => ({ data: null }))
        ]);

        if (treeRes.data) {
          onMessage({ type: 'tree_update', current_count: treeRes.data.current_count });
          if (treeRes.data.tesla_triggered) {
            onMessage({ type: 'tesla_trigger' });
          }
        }
        if (gameRes.data) {
          onMessage({ 
            type: 'game_state', 
            state: gameRes.data.state,
            users_count: gameRes.data.users_count 
          });
        }
        if (raffleRes.data) {
          if (raffleRes.data.state === 'winner' && raffleRes.data.winner) {
            onMessage({ type: 'raffle_winner', name: raffleRes.data.winner });
          }
        }
      } catch (e) {
        console.log('Polling error:', e);
      }
    };

    // Initial poll
    poll();
    
    // Poll every 1 second
    intervalRef.current = setInterval(poll, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, onMessage]);
};

export const useWebSocket = () => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [usePolling, setUsePolling] = useState(false);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;

  const handleMessage = useCallback((data) => {
    setLastMessage(data);
  }, []);

  // Use polling as fallback
  useFallbackPolling(usePolling, handleMessage);

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    try {
      ws.current = new WebSocket(WS_URL);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setUsePolling(false);
        reconnectAttempts.current = 0;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        
        // Attempt reconnect with backoff
        reconnectAttempts.current++;
        if (reconnectAttempts.current <= maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
          reconnectTimeout.current = setTimeout(connect, delay);
        } else {
          // Switch to polling after max attempts
          console.log('Switching to polling fallback');
          setUsePolling(true);
          setIsConnected(true); // Show as connected since polling works
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      // Fall back to polling immediately on error
      setUsePolling(true);
      setIsConnected(true);
    }
  }, [handleMessage]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, []);

  return { isConnected, lastMessage, sendMessage, usePolling };
};

export default useWebSocket;
